const RESERVATION_KEY = 'densia_cart_reservation_end'
const RESERVATION_MS = 10 * 60 * 1000

let reservationIntervalId = null
let cartTriggerOpenInFlight = false
let lastCartTriggerOpenAt = 0

const CART_TRIGGER_GUARD_MS = 800

const formatMoney = (cents) => {
  if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
    return window.Shopify.formatMoney(cents)
  }

  return `$${(cents / 100).toFixed(2)}`
}

const getCartDrawer = () => document.getElementById('cart-drawer')

const openDrawerImmediately = (drawer) => {
  if (!drawer || drawer.open) {
    return
  }

  drawer.setAttribute('open', 'immediate')
}

const buildCartLoadingSkeleton = () => `
  <div class="densia-cart-drawer__loading" data-densia-cart-loading role="status" aria-live="polite" aria-label="Loading cart">
    <div class="densia-cart-drawer__loading-spinner" aria-hidden="true"></div>
    <div class="densia-cart-drawer__loading-lines" aria-hidden="true">
      <span class="densia-cart-drawer__loading-line"></span>
      <span class="densia-cart-drawer__loading-line densia-cart-drawer__loading-line--short"></span>
      <span class="densia-cart-drawer__loading-line"></span>
    </div>
  </div>
`

const setCartLoadingState = (drawer, isLoading) => {
  if (!drawer) {
    return
  }

  drawer.classList.toggle('is-loading', isLoading)

  const items = drawer.querySelector('.densia-cart-drawer__items')
  if (!items) {
    return
  }

  let loadingNode = items.querySelector('[data-densia-cart-loading]')

  if (isLoading) {
    if (!loadingNode) {
      items.insertAdjacentHTML('afterbegin', buildCartLoadingSkeleton())
      loadingNode = items.querySelector('[data-densia-cart-loading]')
    }
    loadingNode.hidden = false
    return
  }

  if (loadingNode) {
    loadingNode.remove()
  }
}

const parseBottlesFromLabel = (label) => {
  if (!label || typeof label !== 'string') {
    return 0
  }

  const down = label.toLowerCase().trim()
  const hyphenLead = down.split('-')[0]?.trim() || ''
  const hyphenNum = parseInt(hyphenLead, 10)
  if (!Number.isNaN(hyphenNum) && hyphenNum > 0 && String(hyphenNum) === hyphenLead) {
    return hyphenNum
  }

  const spaceLead = down.split(/\s+/)[0]?.trim() || ''
  const spaceNum = parseInt(spaceLead, 10)
  if (!Number.isNaN(spaceNum) && spaceNum > 0 && String(spaceNum) === spaceLead) {
    return spaceNum
  }

  return 0
}

const getUnitsInPack = (item) => {
  const properties = item.properties || {}
  for (const [key, value] of Object.entries(properties)) {
    if (!key || !value) {
      continue
    }
    if (!key.toLowerCase().includes('bottle')) {
      continue
    }
    const propBottles = parseInt(String(value), 10)
    if (!Number.isNaN(propBottles) && propBottles > 0) {
      return propBottles
    }
  }

  const sellingPlanName =
    item.selling_plan_allocation?.selling_plan?.name ||
    item.selling_plan_allocation?.selling_plan_name ||
    ''
  const planBottles = parseBottlesFromLabel(sellingPlanName)
  if (planBottles > 0) {
    return planBottles
  }

  const variantBottles = parseBottlesFromLabel(item.variant_title || '')
  if (variantBottles > 0) {
    return variantBottles
  }

  return 1
}

const isMainProductLine = (item, mainHandle) => {
  const productHandle = item.handle || item.product_handle || ''

  if (productHandle === mainHandle) {
    return true
  }

  return productHandle.toLowerCase().includes('densiplex')
}

const getBottleQty = (cart, mainHandle, protectionHandle) => {
  if (!cart?.items) {
    return 0
  }

  return cart.items.reduce((sum, item) => {
    const productHandle = item.handle || item.product_handle || ''

    if (productHandle === protectionHandle) {
      return sum
    }

    if (!isMainProductLine(item, mainHandle)) {
      return sum
    }

    const unitsInPack = getUnitsInPack(item)
    const lineQty = item.quantity || 1

    return sum + unitsInPack * lineQty
  }, 0)
}

const readReservationEnd = () => {
  const stored = sessionStorage.getItem(RESERVATION_KEY)
  if (!stored) {
    return null
  }

  const end = parseInt(stored, 10)
  return Number.isNaN(end) ? null : end
}

/** Démarre le chrono une seule fois par session (premier article au panier). */
const startReservationIfNeeded = () => {
  const existing = readReservationEnd()
  if (existing !== null) {
    return existing
  }

  const end = Date.now() + RESERVATION_MS
  sessionStorage.setItem(RESERVATION_KEY, String(end))
  return end
}

const clearReservationEnd = () => {
  sessionStorage.removeItem(RESERVATION_KEY)
}

const formatReservationTime = (msLeft) => {
  const totalSec = Math.max(0, Math.ceil(msLeft / 1000))
  const minutes = Math.floor(totalSec / 60)
  const seconds = totalSec % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

const hasStaticReservationTimer = (drawer) =>
  Boolean(drawer?.querySelector('[data-densia-reservation-timer][data-densia-reservation-static]'))

const syncReservationTimer = (drawer) => {
  const timerEl = drawer?.querySelector('[data-densia-reservation-timer]')
  if (!timerEl || timerEl.hasAttribute('data-densia-reservation-static')) {
    return
  }

  const end = readReservationEnd()
  if (end === null) {
    timerEl.textContent = formatReservationTime(RESERVATION_MS)
    return
  }

  const msLeft = end - Date.now()
  timerEl.textContent = formatReservationTime(msLeft)
}

const stopReservationTimer = () => {
  if (reservationIntervalId !== null) {
    clearInterval(reservationIntervalId)
    reservationIntervalId = null
  }
}

const getVariantCompareMap = (drawer) => {
  if (!drawer?.dataset?.variantComparePrices) {
    return {}
  }

  try {
    return JSON.parse(drawer.dataset.variantComparePrices)
  } catch {
    return {}
  }
}

const getCartSavingsCents = (cart, drawer) => {
  if (!cart?.items) {
    return 0
  }

  const compareMap = getVariantCompareMap(drawer)

  return cart.items.reduce((sum, item) => {
    const originalLine = item.original_line_price || 0
    const finalLine = item.final_line_price || 0

    if (originalLine > finalLine) {
      return sum + (originalLine - finalLine)
    }

    const variantId = String(item.variant_id || '')
    const compareAt =
      compareMap[variantId] || item.compare_at_price || item.variant?.compare_at_price || 0
    const finalUnit = item.final_price || item.discounted_price || item.price || 0
    const quantity = item.quantity || 1

    if (compareAt > finalUnit) {
      return sum + (compareAt - finalUnit) * quantity
    }

    return sum
  }, 0)
}

const startReservationTimer = (drawer) => {
  if (!drawer || !drawer.querySelector('[data-densia-reservation-timer]') || hasStaticReservationTimer(drawer)) {
    return
  }

  stopReservationTimer()
  syncReservationTimer(drawer)
  reservationIntervalId = setInterval(() => syncReservationTimer(drawer), 1000)
}

const updateCartDrawer = (cart) => {
  const drawer = getCartDrawer()
  if (!drawer || !cart) {
    return
  }

  const mainHandle = drawer.dataset.mainHandle || ''
  const protectionHandle = drawer.dataset.protectionHandle || ''
  const bottleQty = getBottleQty(cart, mainHandle, protectionHandle)

  drawer.dataset.bottleQty = String(bottleQty)

  const savingsRow = drawer.querySelector('[data-densia-savings-row]')
  const savingsAmount = drawer.querySelector('[data-densia-savings-amount]')
  const subtotalEl = drawer.querySelector('[data-densia-subtotal]')

  if (typeof cart.total_price === 'number') {
    const formattedTotal = formatMoney(cart.total_price)
    if (subtotalEl) {
      subtotalEl.textContent = formattedTotal
    }
  }

  const savingsCents = getCartSavingsCents(cart, drawer)
  const serverSavingsCents = parseInt(savingsRow?.dataset.densiaSavingsCents || '0', 10)
  const effectiveSavings = savingsCents > 0 ? savingsCents : serverSavingsCents

  if (savingsRow && savingsAmount && effectiveSavings > 0) {
    savingsRow.hidden = false
    savingsAmount.textContent = `-${formatMoney(effectiveSavings)}`
  }

  if (cart.item_count > 0) {
    startReservationIfNeeded()
    syncReservationTimer(drawer)
    if (drawer.open) {
      startReservationTimer(drawer)
    }
  } else {
    clearReservationEnd()
    stopReservationTimer()
  }
}

const handleDensiaCartQtyClick = (event) => {
  const button = event.target.closest('[data-densia-qty-action]')
  if (!button) {
    return
  }

  const quantityRoot = button.closest('line-item-quantity')
  if (!quantityRoot) {
    return
  }

  const input = quantityRoot.querySelector('.quantity-input[data-line-key]')
  if (!input) {
    return
  }

  const step = parseInt(input.step || '1', 10) || 1
  const min = parseInt(input.min || '0', 10)
  const max = input.max ? parseInt(input.max, 10) : null
  let value = parseInt(input.value, 10) || 0

  if (button.dataset.densiaQtyAction === 'increase') {
    value += step
    if (max !== null) {
      value = Math.min(value, max)
    }
  } else {
    value -= step
    value = Math.max(value, min)
  }

  if (value === parseInt(input.value, 10)) {
    return
  }

  input.value = String(value)
  input.dispatchEvent(new Event('change', { bubbles: true }))
}

const handleCartChange = (event) => {
  const cart = event.detail?.cart
  if (cart) {
    updateCartDrawer(cart)
  }
}

const handleDrawerAfterShow = (event) => {
  if (event.target?.id !== 'cart-drawer') {
    return
  }

  const drawer = getCartDrawer()
  if (!drawer || !drawer.querySelector('[data-densia-reservation-timer]') || hasStaticReservationTimer(drawer)) {
    return
  }

  startReservationIfNeeded()
  syncReservationTimer(drawer)
  startReservationTimer(drawer)
}

const resetCheckoutCtaLoading = () => {
  getCartDrawer()?.querySelector('[data-densia-checkout-cta]')?.classList.remove('is-loading')
}

const handleCheckoutCtaClick = (event) => {
  const cta = event.target.closest('[data-densia-checkout-cta]')
  const drawer = getCartDrawer()

  if (!cta || !drawer?.contains(cta)) {
    return
  }

  window.setTimeout(() => {
    cta.classList.add('is-loading')
  }, 50)
}

const handleDrawerAfterHide = (event) => {
  if (event.target?.id !== 'cart-drawer') {
    return
  }

  setCartLoadingState(getCartDrawer(), false)
  resetCheckoutCtaLoading()
  stopReservationTimer()
}

const handleCartTriggerClick = (event) => {
  const trigger = event.target.closest('[aria-controls="cart-drawer"]')
  if (!trigger) {
    return
  }

  if (event.type === 'pointerdown' && event.pointerType && event.pointerType !== 'touch') {
    return
  }

  const drawer = getCartDrawer()
  if (!drawer) {
    return
  }

  if (drawer.open) {
    return
  }

  const now = Date.now()
  if (cartTriggerOpenInFlight && now - lastCartTriggerOpenAt < CART_TRIGGER_GUARD_MS) {
    event.preventDefault()
    event.stopPropagation()
    return
  }

  event.preventDefault()
  event.stopPropagation()

  lastCartTriggerOpenAt = now
  cartTriggerOpenInFlight = true

  openDrawerImmediately(drawer)

  setCartLoadingState(drawer, true)

  fetch(`${window.Shopify.routes.root}cart.js`)
    .then((res) => res.json())
    .then((cart) => {
      updateCartDrawer(cart)
    })
    .catch(() => {})
    .finally(() => {
      setCartLoadingState(drawer, false)
      setTimeout(() => {
        cartTriggerOpenInFlight = false
      }, CART_TRIGGER_GUARD_MS)
    })
}

const openCartIfQueryParam = () => {
  const params = new URLSearchParams(window.location.search)
  if (params.get('cart') !== 'open') {
    return
  }

  const drawer = getCartDrawer()
  if (drawer && typeof drawer.show === 'function') {
    drawer.show()
  }
}

const initDensiaCartDrawer = () => {
  openCartIfQueryParam()

  const drawer = getCartDrawer()
  if (!drawer) {
    return
  }

  drawer.addEventListener('dialog:after-show', handleDrawerAfterShow)
  drawer.addEventListener('dialog:after-hide', handleDrawerAfterHide)

  fetch(`${window.Shopify.routes.root}cart.js`)
    .then((res) => res.json())
    .then((cart) => updateCartDrawer(cart))
    .catch(() => {})
}

document.addEventListener('click', handleDensiaCartQtyClick)
document.addEventListener('click', handleCheckoutCtaClick)
document.addEventListener('pointerdown', handleCartTriggerClick, true)
document.addEventListener('click', handleCartTriggerClick, true)
window.addEventListener('popstate', resetCheckoutCtaLoading)
window.addEventListener('pageshow', resetCheckoutCtaLoading)
document.addEventListener('cart:change', handleCartChange)
document.addEventListener('variant:add', handleCartChange)
document.addEventListener('cart:refresh', () => {
  fetch(`${window.Shopify.routes.root}cart.js`)
    .then((res) => res.json())
    .then((cart) => updateCartDrawer(cart))
    .catch(() => {})
})

;['cart:updated', 'cart:added', 'cart:removed'].forEach((eventName) => {
  document.addEventListener(eventName, () => {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh'))
  })
})

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDensiaCartDrawer)
} else {
  initDensiaCartDrawer()
}
